'use client';

import type { ElementType } from 'react';

import React, { forwardRef } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

interface ParagraphProps {
  children?: React.ReactNode;
  innerHtml?: string;
  className?: string;
  size?: 'sm' | 'base' | 'lg' | 'xl' | '2xl';
  tag?: ElementType;
  color?: 'white' | 'muted-foreground' | 'muted' | 'black' | 'primary' | 'foreground' | 'secondary';
  onClick?: () => void;
}

export const Paragraph = forwardRef<HTMLDivElement, ParagraphProps>(
  (
    { children, innerHtml, className, size = 'lg', color = 'secondary', tag: Tag = 'div', onClick },
    ref,
  ) => {
    if (innerHtml) {
      return (
        <HtmlRendererRaw
          as={Tag}
          ref={ref}
          onClick={onClick}
          className={cn(
            'tracking-tight transition-all',
            'ea-paragraph__inner-html',
            `ea-paragraph__inner-${size}`,
            `ea-paragraph__inner-${color}`,
            onClick && 'cursor-pointer text-opacity-80',
            className,
          )}
          html={innerHtml}
        />
      );
    }

    return (
      <Tag
        ref={ref}
        onClick={onClick}
        className={cn(
          'ea-paragraph',
          'tracking-tight transition-all',
          size === 'sm' && 'text-xs sm:text-sm',
          size === 'base' && 'text-sm sm:text-base',
          size === 'lg' && 'text-base leading-[150%] sm:text-lg sm:leading-[150%]',
          size === 'xl' && 'text-lg lg:text-xl',
          size === '2xl' && 'text-lg sm:text-xl lg:text-2xl',
          color === 'white' && 'text-white',
          color === 'muted-foreground' && 'text-[#666666]',
          color === 'black' && 'text-black',
          color === 'muted' && 'text-muted',
          color === 'foreground' && 'text-foreground',
          color === 'primary' && 'text-primary',
          color === 'secondary' && 'text-secondary',
          onClick && 'cursor-pointer text-opacity-80',
          className,
        )}
      >
        {children}
      </Tag>
    );
  },
);

Paragraph.displayName = 'Paragraph';
